import { useEffect, useRef, useState, useCallback } from "react";
import { Tooltip } from "react-tooltip";
import { useLocation } from "react-router-dom";
import { InfoCircleOutlined } from "@ant-design/icons";
import Navbar from "../Component/LandingV2/Navbar";
import Loader from "../Component/LandingV2/Loader";
import MiniFooter from "../Component/LandingV2/MiniFooter";
import PriceCard from "../Component/Trending/PriceCard";
import TrendingEventCard from "../Component/Trending/TrendingEventCard";
import TickerFilter from "../Component/Trending/TickerFilter";
import StockVsIndexCard from "../Component/Trending/StockVsIndexCard";
import { Chip, Modal, Box } from "@mui/material";
import "./TrendingPage.scss";
import { AlertData, AlertsJson, QuotesJson } from "../types/alerts.json";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import DateFilter, { DateFilterValue } from "../Component/Trending/DateFilter";

const highlightBoxEnum = {
  PriceChange: 0,
  StockVsIndex: 1,
  OpenPrices: 2,
  LastTradedPrice: 3,
  StockVolume: 4,
  HotEvents: 5,
  AccumulationDistribution: 6,
  DPO: 7,
  AverageTrueRange: 8,
  AverageDirectionalIndex: 9,
  VolumeWeightedAveragePrice: 10,
  MomentumChange: 11,
  NewsStories: 12,
  NewsCount: 13,
  Quotes: 14,
};
// useState
const infoBoxText = {
  [highlightBoxEnum.PriceChange]:
    "Shows how much a stock's price went up or down compared to yesterday's closing price.",
  [highlightBoxEnum.StockVsIndex]:
    "Compares a company's stock performance against its industry index to see if the stock is moving in line with the broader sector.",
  [highlightBoxEnum.OpenPrices]:
    "Shows today's opening price compared to the highest or lowest opening prices of the last 15 days.",
  [highlightBoxEnum.LastTradedPrice]:
    "Shows today's closing price compared to the highest or lowest closing prices of the last 15 days.",
  [highlightBoxEnum.StockVolume]:
    "Highlights when a stock has unusually high or low trading activity today compared to its typical volume at this exact time over the last 7 days.",
  [highlightBoxEnum.HotEvents]:
    "Highlights key news and corporate events for companies that are currently trending.",
  [highlightBoxEnum.AccumulationDistribution]:
    "Tracks if investors are buying (accumulating) or selling (distributing) a stock, signaling whether the stock is seeing positive demand or sell-offs.",
  [highlightBoxEnum.DPO]:
    "Helps identify short-term price cycles and patterns, helping you spot regular peaks and troughs in the stock's price history.",
  [highlightBoxEnum.AverageTrueRange]:
    "Measures a stock's volatility. A high alert means the stock is experiencing larger price swings than usual, while a low alert means it is relatively stable.",
  [highlightBoxEnum.AverageDirectionalIndex]:
    "Measures the strength of a price trend. A value above 40 means a strong trend, while a value below 15 means it's trading flat.",
  [highlightBoxEnum.VolumeWeightedAveragePrice]:
    "Shows if a stock is trading in an uptrend (above its 14-day average price range) or a downtrend (below its 14-day range).",
  [highlightBoxEnum.MomentumChange]:
    "Alerts you when multiple momentum indicators agree that a stock is overbought (potentially due for a drop) or oversold (potentially due for a bounce).",
  [highlightBoxEnum.Quotes]:
    "Displays a quick summary of the stock's current price, market capitalization, and daily trading volume.",
  [highlightBoxEnum.NewsCount]:
    "Shows the number of news articles written about a company today compared to its weekly average, helping you gauge media interest.",
  [highlightBoxEnum.NewsStories]:
    "Highlights major news articles where a company is frequently mentioned, signaling high public or media focus.",
};

const Trending = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.state && location.state.ticker) {
      setTickerFilter(location.state.ticker);
    }
  }, [location.state]);

  const scrollTabs = (tabName: string) => {
    const container = document.querySelector('.trending-sticky-tabs');
    if (container) {
      if (tabName === 'Technical Signals' || tabName === 'News & Events') {
        container.scrollTo({ left: 300, behavior: 'smooth' });
      } else {
        container.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }
  };

  const [vwapSortOrder, setVwapSortOrder] = useState("asc");
  const [vwapValueSortOrder, setVwapValueSortOrder] = useState("asc");
  const [activeVwapSortType, setActiveVwapSortType] = useState("ticker");

  // -----------
  const [adxSortOrder, setAdxSortOrder] = useState("asc"); // For sorting by ticker
  const [adxValueSortOrder, setAdxValueSortOrder] = useState("asc"); // For sorting by ADX value
  const [activeAdxSortType, setActiveAdxSortType] = useState("ticker"); // Track the active sort type

  // -----------

  const [atrSortOrder, setAtrSortOrder] = useState("asc"); // For sorting by ticker
  const [atrValueSortOrder, setAtrValueSortOrder] = useState("asc"); // For sorting by ATR value
  const [activeAtrSortType, setActiveAtrSortType] = useState("ticker"); // Track the active sort type

  // -----------
  const [volumeSortOrder, setVolumeSortOrder] = useState("asc"); // For sorting by ticker
  const [volumeValueSortOrder, setVolumeValueSortOrder] = useState("asc"); // For sorting by volume value
  const [activeVolumeSortType, setActiveVolumeSortType] = useState("ticker"); // Track the active sort type

  // -----------
  const [adSortOrder, setAdSortOrder] = useState("asc"); // For sorting by ticker
  const [adValueSortOrder, setAdValueSortOrder] = useState("asc"); // For sorting by volume value
  const [activeAdSortType, setActivAdSortType] = useState("ticker"); // Track the active sort type

  // -----------
  const [dpoSortOrder, setDpoSortOrder] = useState("asc"); // For sorting by ticker
  const [dpoValueSortOrder, setDpoValueSortOrder] = useState("asc"); // For sorting by DPO value
  const [activeDpoSortType, setActiveDpoSortType] = useState("ticker"); // Track the active sort type
  // -----------



  // -----------
  const [closePriceSortOrder, setClosePriceSortOrder] = useState("asc"); // For ticker sorting
  const [closeValueSortOrder, setCloseValueSortOrder] = useState("asc"); // For new value sorting
  const [oldCloseValueSortOrder, setOldCloseValueSortOrder] = useState("asc"); // For old value sorting
  const [activeCloseSortType, setActiveCloseSortType] = useState("ticker"); // Track which column is currently sorted

  // -----------
  const [openPriceSortOrder, setOpenPriceSortOrder] = useState("asc");
  const [openValueSortOrder, setOpenValueSortOrder] = useState("asc");
  const [oldValueSortOrder, setOldValueSortOrder] = useState("asc");
  const [activeSortType, setActiveSortType] = useState("ticker");

  // -----------
  const [momentumSortOrder, setMomentumSortOrder] = useState("asc");
  const [momentumValueSortOrder, setMomentumValueSortOrder] = useState("asc");

  // const [priceChangeSortOrder, setPriceChangeSortOrder] = useState('asc');
  const [priceChangeTickerSortOrder, setPriceChangeTickerSortOrder] = useState<
    "asc" | "desc"
  >("asc");
  const [priceChangeValueSortOrder, setPriceChangeValueSortOrder] = useState<
    "asc" | "desc" | null
  >(null);
  const [quotes, setQuotes] = useState<QuotesJson>([]);
  const [data, setData] = useState<AlertData | null>(null);
  const [tickerFilter, setTickerFilter] = useState<string | null>(null);
  const [showInfoBox, setShowInfoBox] = useState(false);
  const [showAllNews, setShowAllNews] = useState(false);
  const [openQuotesModal, setOpenQuotesModal] = useState(false);
  // always keep a numeric index for the highlighted box; default to first entry
  const [highlightBox, sethighlightBox] = useState<number>(
    highlightBoxEnum.PriceChange,
  );
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const disclaimerModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showDisclaimer && disclaimerModalRef.current) {
      disclaimerModalRef.current.focus();
    }
  }, [showDisclaimer]);
  // const cardRef = useRef<HTMLDivElement>(null);
  const infoBoxRefList = useRef<(HTMLDivElement | null)[]>([]);
  const [searchTicker, setSearchTicker] = useState("");
  const setInfoBoxRef = (index: number) => (ref: HTMLDivElement | null) => {
    infoBoxRefList.current[index] = ref;
  };
  const [modalSearchTicker, setModalSearchTicker] = useState("");

  // STATES
  const [stockNameSortOrder, setStockNameSortOrder] = useState<
    "asc" | "desc" | null
  >("asc");
  const [newsSortOrder, setNewsSortOrder] = useState<"asc" | "desc" | null>(
    null,
  );
  const [weeklyAvgSortOrder, setWeeklyAvgSortOrder] = useState<
    "asc" | "desc" | null
  >(null);

  const [volumeAlertSortOrder, setVolumeAlertSortOrder] = useState("asc");
  const [adAlertSortOrder, setAdAlertSortOrder] = useState("asc");
  const [dpoAlertSortOrder, setDpoAlertSortOrder] = useState("asc");
  const [atrAlertSortOrder, setAtrAlertSortOrder] = useState("asc");
  const [adxAlertSortOrder, setAdxAlertSortOrder] = useState("asc");
  const [vwapAlertSortOrder, setVwapAlertSortOrder] = useState("asc");

  const toggleVolumeAlertSort = () => {
    setVolumeAlertSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActiveVolumeSortType("alert");
    setVolumeSortOrder("asc");
    setVolumeValueSortOrder("asc");
  };

  const toggleAdAlertSort = () => {
    setAdAlertSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActivAdSortType("alert");
    setAdSortOrder("asc");
    setAdValueSortOrder("asc");
  };

  const toggleDpoAlertSort = () => {
    setDpoAlertSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActiveDpoSortType("alert");
    setDpoSortOrder("asc");
    setDpoValueSortOrder("asc");
  };

  const toggleAtrAlertSort = () => {
    setAtrAlertSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActiveAtrSortType("alert");
    setAtrSortOrder("asc");
    setAtrValueSortOrder("asc");
  };

  const toggleAdxAlertSort = () => {
    setAdxAlertSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActiveAdxSortType("alert");
    setAdxSortOrder("asc");
    setAdxValueSortOrder("asc");
  };

  const toggleVwapAlertSort = () => {
    setVwapAlertSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActiveVwapSortType("alert");
    setVwapSortOrder("asc");
    setVwapValueSortOrder("asc");
  };

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); //top stories search
  const [activeTab, setActiveTab] = useState("Market Overview");

  // ── Drag resize for left/right panels ──
  const [rightPanelWidth, setRightPanelWidth] = useState(38); // percentage
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingPanel = useRef(false);

  const onPanelDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingPanel.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (ev: MouseEvent) => {
      if (!isDraggingPanel.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((rect.right - ev.clientX) / rect.width) * 100;
      setRightPanelWidth(Math.min(Math.max(pct, 25), 55));
    };

    const onUp = () => {
      isDraggingPanel.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  const isSearchMatch = (...fields: (string | undefined)[]) => {
    if (!tickerFilter) return true;
    const query = tickerFilter.toLowerCase();
    return fields.some((field) => field && String(field).toLowerCase().includes(query));
  };
  // SORT FUNCTIONS
  const toggleStockNameSort = () => {
    setStockNameSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setNewsSortOrder(null);
    setWeeklyAvgSortOrder(null);
  };

  const toggleNewsSort = () => {
    setNewsSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setStockNameSortOrder(null);
    setWeeklyAvgSortOrder(null);
  };

  const toggleWeeklyAvgSort = () => {
    setWeeklyAvgSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setStockNameSortOrder(null);
    setNewsSortOrder(null);
  };
  const handleInfoBox = (boxType: number) => {
    // when the same box is clicked while already visible, just close the info
    if (showInfoBox && highlightBox === boxType) {
      setShowInfoBox(false);
    } else {
      sethighlightBox(boxType);
      setShowInfoBox(true);
    }
  };


  useEffect(() => {
    if (showInfoBox) {
      if (highlightBox >= 0 && highlightBox <= 5) {
        setActiveTab("Market Overview");
      } else if (highlightBox >= 6 && highlightBox <= 11) {
        setActiveTab("Technical Signals");
      } else if (highlightBox >= 12 && highlightBox <= 15) {
        setActiveTab("News & Events");
      }
    }
  }, [highlightBox, showInfoBox]);

  const [openStoriesModal, setOpenStoriesModal] = useState(false);
  //datefilter
  const [filter, setFilter] = useState<DateFilterValue | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilterValue | null>(null);
  const renderTopStories = (
    limit?: number,
    applyDateFilter: boolean = false,
  ) => {
    if (!data?.Top_stories_using_TSA_count) {
      return <div className="no-data">NO STORY !</div>;
    }

    const query = searchQuery.toLowerCase().trim();

    const filteredStories = Object.keys(data.Top_stories_using_TSA_count)
      .filter((key) => {
        const story = data.Top_stories_using_TSA_count[key];
        if (!isSearchMatch(story?.stock_symbol)) return false;
        if (applyDateFilter && dateFilter) {
          const storyDate = new Date(story?.news_date);
          const from = new Date(dateFilter.from);
          const to = new Date(dateFilter.to + "T23:59:59");
          if (storyDate < from || storyDate > to) return false;
        }
        //  Search filter — only when applyDateFilter=true (modal), searches all fields
        if (applyDateFilter && query) {
          return Object.values(story).some((val) =>
            String(val).toLowerCase().includes(query),
          );
        }
        return true;
      })
      .slice(0, limit);

    if (filteredStories.length === 0) {
      return <div className="no-data">NO STORY !</div>;
    }

    return filteredStories.map((key, i) => {
      const story = data.Top_stories_using_TSA_count[key];
      const titleParts = story?.title?.split(" - ");
      const storyTitle = titleParts?.length > 1 ? titleParts.slice(0, -1).join(" - ") : story?.title;
      const storySource = titleParts?.length > 1 ? titleParts[titleParts.length - 1] : "";

      return (
        <a
          className="story"
          key={i}
          href={story?.link}
          target="_blank"
          rel="noreferrer"
        >
          <div className="story-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" /><path d="M18 14h-8" /><path d="M15 18h-5" /></svg>
          </div>
          <div className="story-content">
            <div className="date-time">{story?.news_date}</div>
            <div className="story-title">{storyTitle}</div>
          </div>
          {storySource && <div className="story-source">{storySource}</div>}
        </a>
      );
    });
  };

  const fetchData = async () => {
    try {
      const response = await fetch(
        (process.env.REACT_APP_COMPANY_DOC || "") + "/Alerts/alerts.json",
      );
      // console.log("--------------------------------", await response.json());
      // const data: AlertsJson = await response.json();
      const data: AlertsJson = await response.json();

      const quotes_response = await fetch(
        (process.env.REACT_APP_COMPANY_DOC || "") +
        "/Alerts/quotes_alerts.json",
      );
      const quotesData: QuotesJson = await quotes_response.json();
      setQuotes(quotesData);

      const latestDate = Object.keys(data).sort().reverse()[0];

      setData({ ...data[latestDate], date: latestDate });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Loading state
  if (!data) {
    return (
      <>
        <Navbar />
        <div className="trending-loader">
          <Loader />
          <p className="trending-loader-title">Loading market signals…</p>
          <p className="trending-loader-subtitle">
            Crunching the latest alerts and price movements for you.
          </p>
        </div>
      </>
    );
  }

  // Create list of open highest difference
  // List of highest
  const combinedOpenValues = Object.keys(
    data.price_change_alerts.alerts_open_highest,
  ).map((key) => {
    return {
      ticker: key,
      ...data.price_change_alerts.alerts_open_highest[key],
      minmax: data.price_change_alerts.alerts_open_highest[key].max_open || 0,
    };
  });
  // Concat lowest
  combinedOpenValues.push(
    ...Object.keys(data.price_change_alerts.alerts_open_lowest).map((key) => {
      return {
        ticker: key,
        ...data.price_change_alerts.alerts_open_lowest[key],
        minmax: data.price_change_alerts.alerts_open_lowest[key].min_open || 0,
      };
    }),
  );

  // Sort by difference between live day and minmax
  combinedOpenValues
    .sort((a, b) => {
      return (
        Math.abs(a.live_day_open - a.minmax) -
        Math.abs(b.live_day_open - b.minmax)
      );
    })
    .reverse();

  // console.log(combinedOpenValues);

  // Create list of close highest difference
  // List of highest
  const combinedCloseValues = Object.keys(
    data.price_change_alerts.alerts_close_highest,
  ).map((key) => {
    return {
      ticker: key,
      ...data.price_change_alerts.alerts_close_highest[key],
      minmax: data.price_change_alerts.alerts_close_highest[key].max_close || 0,
    };
  });
  // Concat lowest
  combinedCloseValues.push(
    ...Object.keys(data.price_change_alerts.alerts_close_lowest).map((key) => {
      return {
        ticker: key,
        ...data.price_change_alerts.alerts_close_lowest[key],
        minmax:
          data.price_change_alerts.alerts_close_lowest[key].min_close || 0,
      };
    }),
  );

  const combinedDPO = Object.keys(data.dpo_alert?.Downward_cycle || {}).map(
    (key) => {
      return {
        DPO_TYPE: "down",
        ticker: key,
        ...data.dpo_alert?.Downward_cycle[key],
        minmax: data.dpo_alert?.Downward_cycle[key].lower_limit || 0, // Correct property
      };
    },
  );

  // Concat lowest (Upward cycle)
  combinedDPO.push(
    ...Object.keys(data.dpo_alert?.Upward_cycle || {}).map((key) => {
      return {
        DPO_TYPE: "up",
        ticker: key,
        ...data.dpo_alert?.Upward_cycle[key],
        minmax: data.dpo_alert?.Upward_cycle[key].upper_limit || 0, // Correct property
      };
    }),
  );

  console.log("Accumulation Data:", data.AD_alert?.Accumulation);
  console.log("Distribution Data:", data.AD_alert?.Distribution);

  const combinedAD = Object.keys(data.AD_alert?.Accumulation || {}).map(
    (key) => {
      return {
        AD_TYPE: "accumulation",
        ticker: key,
        ...data.AD_alert?.Accumulation[key],
        minmax: data.AD_alert?.Accumulation[key].Max_AD || 0, // Correct property (Max_AD or Min_AD)
      };
    },
  );

  console.log("----------AD----", data.AD_alert?.Distribution); // Check if the data is present

  // Concat Distribution data
  combinedAD.push(
    ...Object.keys(data.AD_alert?.Distribution || {}).map((key) => {
      return {
        AD_TYPE: "distribution",
        ticker: key,
        ...data.AD_alert?.Distribution[key],
        minmax: data.AD_alert?.Distribution[key].Min_AD || 0, // Correct property (Max_AD or Min_AD)
      };
    }),
  );

  const combined_Adx_values = data.ADX_change
    ? Object.keys(data.ADX_change.strong_trend).map((key) => {
      return {
        ticker: key,
        trendType: "strong trend", // Add the trend type
        ...data.ADX_change!.strong_trend[key],
      };
    })
    : [];

  // Concat weak trend stocks if ADX_change is available
  if (data.ADX_change) {
    combined_Adx_values.push(
      ...Object.keys(data.ADX_change.weak_trend).map((key) => {
        return {
          ticker: key,
          trendType: "weak trend", // Add the trend type
          ...data.ADX_change!.weak_trend[key],
        };
      }),
    );
  }

  // ATR_change
  const combined_Atr_values = data.ATR_change?.high_volatility
    ? Object.keys(data.ATR_change.high_volatility).map((key) => ({
      ticker: key,
      volatilityType: "high_volatility",
      ...data.ATR_change!.high_volatility[key],
    }))
    : [];

  if (data.ATR_change?.low_volatility) {
    combined_Atr_values.push(
      ...Object.keys(data.ATR_change.low_volatility).map((key) => ({
        ticker: key,
        volatilityType: "low_volatility",
        ...data.ATR_change!.low_volatility[key],
      })),
    );
  }

  // StockVolume_change
  const combined_volume_change = data.StockVolume_change?.Stock_volume_high
    ? Object.keys(data.StockVolume_change.Stock_volume_high).map((key) => {
      return {
        ticker: key,
        volume_type: "high_volume",
        ...data.StockVolume_change!.Stock_volume_high[key],
      };
    })
    : [];

  if (data.StockVolume_change?.Stock_volume_low) {
    combined_volume_change.push(
      ...Object.keys(data.StockVolume_change.Stock_volume_low).map((key) => {
        return {
          ticker: key,
          volume_type: "low_volume",
          ...data.StockVolume_change!.Stock_volume_low[key],
        };
      }),
    );
  }

  // VWAP_change
  const combined_Vwap = data.VWAP_change?.Uptrend
    ? Object.keys(data.VWAP_change.Uptrend).map((key) => {
      return {
        ticker: key,
        trend_type: "uptrend",
        ...data.VWAP_change!.Uptrend[key],
      };
    })
    : [];

  if (data.VWAP_change?.Downtrend) {
    combined_Vwap.push(
      ...Object.keys(data.VWAP_change.Downtrend).map((key) => {
        return {
          ticker: key,
          trend_type: "downtrend",
          ...data.VWAP_change!.Downtrend[key],
        };
      }),
    );
  }

  // Momentum_change
  const combined_momentum_change = data.Momentum_change?.Overbought
    ? Object.keys(data.Momentum_change.Overbought).map((key) => {
      return {
        ticker: key,
        bought_type: "overbought",
        ...data.Momentum_change!.Overbought[key],
      };
    })
    : [];

  if (data.Momentum_change?.Oversold) {
    combined_momentum_change.push(
      ...Object.keys(data.Momentum_change.Oversold).map((key) => {
        return {
          ticker: key,
          bought_type: "oversold",
          ...data.Momentum_change!.Oversold[key],
        };
      }),
    );
  }

  // Sort by difference
  combinedCloseValues
    .sort((a, b) => {
      return (
        Math.abs(a.live_day_close - a.minmax) -
        Math.abs(b.live_day_close - b.minmax)
      );
    })
    .reverse();

  const allTickers = Array.from(
    new Set(
      quotes
        .filter((q) => q.quote !== "0" && q.context !== "0")
        .map((q) => q.ticker_symbol),
    ),
  )
    .filter(Boolean)
    .sort();

  const filteredTickers = allTickers.filter((ticker) =>
    ticker.toLowerCase().includes(searchTicker.toLowerCase()),
  );
  // List of all unique tickers for filter
  // Sort alphabetically
  const tickerList = [
    ...new Set([
      ...Object.keys(
        data.price_change_alerts?.top_n_price_change?.stock_symbol || {},
      ),
      ...Object.values(data.price_change_alerts?.alerts_open_highest || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.price_change_alerts?.alerts_open_lowest || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(
        data.price_change_alerts?.alerts_close_highest || [],
      ).map((ele) => ele.ticker_symbol),
      ...Object.values(data.price_change_alerts?.alerts_close_lowest || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.ADX_change?.strong_trend || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.ADX_change?.weak_trend || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.ATR_change?.high_volatility || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.ATR_change?.low_volatility || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.AD_alert?.Accumulation || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.AD_alert?.Distribution || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.Momentum_change?.Overbought || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.Momentum_change?.Oversold || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.StockVolume_change?.Stock_volume_high || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.StockVolume_change?.Stock_volume_low || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.VWAP_change?.Downtrend || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.VWAP_change?.Uptrend || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.dpo_alert?.Downward_cycle || []).map(
        (ele) => ele.ticker_symbol,
      ),
      ...Object.values(data.dpo_alert?.Upward_cycle || []).map(
        (ele) => ele.ticker_symbol,
      ),
    ]),
  ].sort();

  const sortedAtrValues = [...combined_Atr_values].sort((a, b) => {
    if (activeAtrSortType === "ticker") {
      return atrSortOrder === "asc"
        ? a.ticker_symbol.localeCompare(b.ticker_symbol)
        : b.ticker_symbol.localeCompare(a.ticker_symbol);
    } else if (activeAtrSortType === "value") {
      return atrValueSortOrder === "asc"
        ? a.Last_ATR - b.Last_ATR
        : b.Last_ATR - a.Last_ATR;
    } else if (activeAtrSortType === "alert") {
      return atrAlertSortOrder === "asc"
        ? a.volatilityType.localeCompare(b.volatilityType)
        : b.volatilityType.localeCompare(a.volatilityType);
    }
    return 0; // Default case
  });

  const toggleAtrSort = () => {
    setAtrSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveAtrSortType("ticker"); // Set active sort type to ticker
    setAtrValueSortOrder("asc"); // Reset value sort order
    setAtrAlertSortOrder("asc");
  };

  const toggleAtrValueSort = () => {
    setAtrValueSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveAtrSortType("value"); // Set active sort type to ATR value
    setAtrSortOrder("asc"); // Reset ticker sort order
    setAtrAlertSortOrder("asc");
  };

  const sortedAdxValues = [...combined_Adx_values].sort((a, b) => {
    if (activeAdxSortType === "ticker") {
      return adxSortOrder === "asc"
        ? a.ticker_symbol.localeCompare(b.ticker_symbol)
        : b.ticker_symbol.localeCompare(a.ticker_symbol);
    } else if (activeAdxSortType === "value") {
      return adxValueSortOrder === "asc" ? a.ADX - b.ADX : b.ADX - a.ADX;
    } else if (activeAdxSortType === "alert") {
      return adxAlertSortOrder === "asc"
        ? a.trendType.localeCompare(b.trendType)
        : b.trendType.localeCompare(a.trendType);
    }
    return 0; // Default case
  });

  const toggleAdxSort = () => {
    setAdxSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveAdxSortType("ticker"); // Set active sort type to ticker
    setAdxValueSortOrder("asc"); // Reset value sort order
    setAdxAlertSortOrder("asc");
  };

  const toggleAdxValueSort = () => {
    setAdxValueSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveAdxSortType("value"); // Set active sort type to ADX value
    setAdxSortOrder("asc"); // Reset ticker sort order
    setAdxAlertSortOrder("asc");
  };

  const sortedVwapValues = [...combined_Vwap].sort((a, b) => {
    if (activeVwapSortType === "ticker") {
      return vwapSortOrder === "asc"
        ? a.ticker_symbol.localeCompare(b.ticker_symbol)
        : b.ticker_symbol.localeCompare(a.ticker_symbol);
    } else if (activeVwapSortType === "value") {
      const aVwap = a.current_vwap; // Already a number
      const bVwap = b.current_vwap; // Already a number

      // Ensure that aVwap and bVwap are valid numbers before performing arithmetic
      if (isNaN(aVwap) || isNaN(bVwap)) {
        // Handle NaN values as needed, for example, consider them equal
        return 0;
      }

      return vwapValueSortOrder === "asc" ? aVwap - bVwap : bVwap - aVwap;
    } else if (activeVwapSortType === "alert") {
      return vwapAlertSortOrder === "asc"
        ? a.trend_type.localeCompare(b.trend_type)
        : b.trend_type.localeCompare(a.trend_type);
    }
    return 0; // Default case
  });

  const toggleVwapSort = () => {
    setVwapSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveVwapSortType("ticker"); // Set active sort type to ticker
    setVwapValueSortOrder("asc"); // Reset value sort order
    setVwapAlertSortOrder("asc");
  };

  const toggleVwapValueSort = () => {
    setVwapValueSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setActiveVwapSortType("value"); // Set active sort type to VWAP value
    setVwapSortOrder("asc"); // Reset ticker sort order
    setVwapAlertSortOrder("asc");
  };

  const sortedVolumeValues = [...combined_volume_change].sort((a, b) => {
    if (activeVolumeSortType === "ticker") {
      return volumeSortOrder === "asc"
        ? a.ticker_symbol.localeCompare(b.ticker_symbol)
        : b.ticker_symbol.localeCompare(a.ticker_symbol);
    } else if (activeVolumeSortType === "volumeValue") {
      return volumeValueSortOrder === "asc"
        ? a.Max_volume - b.Max_volume
        : b.Max_volume - a.Max_volume;
    } else if (activeVolumeSortType === "alert") {
      return volumeAlertSortOrder === "asc"
        ? a.volume_type.localeCompare(b.volume_type)
        : b.volume_type.localeCompare(a.volume_type);
    }
    return 0; // Default case
  });

  const toggleVolumeSort = () => {
    setVolumeSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveVolumeSortType("ticker"); // Set active sort type to ticker
    setVolumeValueSortOrder("asc"); // Reset volume value sort
    setVolumeAlertSortOrder("asc");
  };

  const toggleVolumeValueSort = () => {
    setVolumeValueSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setActiveVolumeSortType("volumeValue"); // Set active sort type to volume value
    setVolumeSortOrder("asc"); // Reset ticker sort
    setVolumeAlertSortOrder("asc");
  };

  const sortedAD = [...combinedAD].sort((a, b) => {
    if (activeAdSortType === "ticker") {
      // Ticker comparison returns a number (localeCompare is fine here)
      return adSortOrder === "asc"
        ? (a.ticker_symbol || "").localeCompare(b.ticker_symbol || "")
        : (b.ticker_symbol || "").localeCompare(a.ticker_symbol || "");
    } else if (activeAdSortType === "value") {
      // Ensure the value comparison returns a number
      const aValue = a["Current A/D"] || 0; // Default to 0 if undefined
      const bValue = b["Current A/D"] || 0; // Default to 0 if undefined
      return adValueSortOrder === "asc" ? aValue - bValue : bValue - aValue;
    } else if (activeAdSortType === "alert") {
      const aAlert = a.AD_TYPE || "";
      const bAlert = b.AD_TYPE || "";
      return adAlertSortOrder === "asc" ? aAlert.localeCompare(bAlert) : bAlert.localeCompare(aAlert);
    }
    return 0; // Default case (no sorting)
  });

  // Toggle sorting by ticker
  const toggleAdTickerSort = () => {
    setAdSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActivAdSortType("ticker"); // Set active sort type to ticker
    setAdValueSortOrder("asc");
    setAdAlertSortOrder("asc");
  };

  // Toggle sorting by value
  const toggleAdValueSort = () => {
    setAdValueSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActivAdSortType("value"); // Set active sort type to value
    setAdSortOrder("asc");
    setAdAlertSortOrder("asc");
  };

  const sortedDPO = [...combinedDPO].sort((a, b) => {
    if (activeDpoSortType === "ticker") {
      return dpoSortOrder === "asc"
        ? (a.ticker_symbol || "").localeCompare(b.ticker_symbol || "")
        : (b.ticker_symbol || "").localeCompare(a.ticker_symbol || "");
    } else if (activeDpoSortType === "value") {
      const aValue = a.Current_dpo || 0; // Default to 0 if undefined
      const bValue = b.Current_dpo || 0; // Default to 0 if undefined
      return dpoValueSortOrder === "asc" ? aValue - bValue : bValue - aValue;
    } else if (activeDpoSortType === "alert") {
      const aAlert = a.DPO_TYPE || "";
      const bAlert = b.DPO_TYPE || "";
      return dpoAlertSortOrder === "asc" ? aAlert.localeCompare(bAlert) : bAlert.localeCompare(aAlert);
    }
    return 0; // Default case
  });

  // Toggle sorting by ticker
  const toggleDpoTickerSort = () => {
    setDpoSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveDpoSortType("ticker"); // Set active sort type to ticker
    setDpoValueSortOrder("asc");
    setDpoAlertSortOrder("asc");
  };

  // Toggle sorting by value
  const toggleDpoValueSort = () => {
    setDpoValueSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveDpoSortType("value"); // Set active sort type to value
    setDpoSortOrder("asc");
    setDpoAlertSortOrder("asc");
  };



  const sortedClosePriceValues = [...combinedCloseValues].sort((a, b) => {
    if (activeCloseSortType === "ticker") {
      return closePriceSortOrder === "asc"
        ? a.ticker_symbol.localeCompare(b.ticker_symbol)
        : b.ticker_symbol.localeCompare(a.ticker_symbol);
    } else if (activeCloseSortType === "newValue") {
      return closeValueSortOrder === "asc"
        ? a.live_day_close - b.live_day_close
        : b.live_day_close - a.live_day_close;
    } else if (activeCloseSortType === "oldValue") {
      return oldCloseValueSortOrder === "asc"
        ? a.minmax - b.minmax
        : b.minmax - a.minmax;
    }
    return 0; // Default case
  });

  const toggleClosePriceSort = () => {
    setClosePriceSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setActiveCloseSortType("ticker"); // Update active sort type
    setCloseValueSortOrder("asc"); // Reset new value sort
    setOldCloseValueSortOrder("asc"); // Reset old value sort
  };

  const toggleCloseValueSort = () => {
    setCloseValueSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setActiveCloseSortType("newValue"); // Update active sort type
    setClosePriceSortOrder("asc"); // Reset ticker sort
    setOldCloseValueSortOrder("asc"); // Reset old value sort
  };

  const toggleOldCloseValueSort = () => {
    setOldCloseValueSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setActiveCloseSortType("oldValue"); // Update active sort type
    setClosePriceSortOrder("asc"); // Reset ticker sort
    setCloseValueSortOrder("asc"); // Reset new value sort
  };

  const sortedOpenPriceValues = [...combinedOpenValues].sort((a, b) => {
    // Check which sort type is active and apply sorting based on it
    if (activeSortType === "ticker") {
      return openPriceSortOrder === "asc"
        ? a.ticker_symbol.localeCompare(b.ticker_symbol)
        : b.ticker_symbol.localeCompare(a.ticker_symbol);
    } else if (activeSortType === "newValue") {
      return openValueSortOrder === "asc"
        ? a.live_day_open - b.live_day_open
        : b.live_day_open - a.live_day_open;
    } else if (activeSortType === "oldValue") {
      return oldValueSortOrder === "asc"
        ? a.minmax - b.minmax
        : b.minmax - a.minmax;
    }
    return 0; // Default case
  });

  const toggleOpenPriceSort = () => {
    setOpenPriceSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setActiveSortType("ticker");
    setOpenValueSortOrder("asc");
    setOldValueSortOrder("asc");
  };

  const toggleOpenValueSort = () => {
    setOpenValueSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setActiveSortType("newValue");
    setOpenPriceSortOrder("asc");
    setOldValueSortOrder("asc");
  };

  const toggleOldValueSort = () => {
    setOldValueSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    setActiveSortType("oldValue");
    setOpenPriceSortOrder("asc");
    setOpenValueSortOrder("asc");
  };

  const sortedMomentumValues = [...combined_momentum_change].sort((a, b) => {
    if (momentumSortOrder === "asc") {
      return a.ticker_symbol.localeCompare(b.ticker_symbol);
    } else if (momentumSortOrder === "desc") {
      return b.ticker_symbol.localeCompare(a.ticker_symbol);
    }

    // Sorting by value (MFI)
    if (momentumValueSortOrder === "asc") {
      return a.MFI - b.MFI;
    } else if (momentumValueSortOrder === "desc") {
      return b.MFI - a.MFI;
    }

    return 0; // default
  });

  const toggleMomentumSort = () => {
    setMomentumSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };
  const toggleMomentumValueSort = () => {
    setMomentumValueSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setMomentumSortOrder("asc"); // Reset ticker sort when value sort is toggled
  };

  const sortedPriceChangeValues = Object.keys(
    data.price_change_alerts.top_n_price_change.stock_symbol,
  ).sort((a, b) => {
    if (priceChangeValueSortOrder === null) {
      if (priceChangeTickerSortOrder === "asc") {
        return a.localeCompare(b); // Ascending alphabetical order
      } else {
        return b.localeCompare(a); // Descending alphabetical order
      }
    } else {
      // If value sorting is active, we don't sort by ticker
      return 0;
    }
  });

  // Sort by value (% change) if value sorting is active
  if (priceChangeValueSortOrder !== null) {
    sortedPriceChangeValues.sort((a, b) => {
      const aValue =
        data.price_change_alerts.top_n_price_change.stock_symbol[a];
      const bValue =
        data.price_change_alerts.top_n_price_change.stock_symbol[b];

      if (priceChangeValueSortOrder === "asc") {
        return aValue - bValue; // Ascending by value
      } else {
        return bValue - aValue; // Descending by value
      }
    });
  }

  // Toggling functions
  const togglePriceChangeTickerSort = () => {
    setPriceChangeTickerSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setPriceChangeValueSortOrder(null); // Reset value sort when ticker sort is toggled
  };

  const togglePriceChangeValueSort = () => {
    setPriceChangeValueSortOrder((prevOrder) =>
      prevOrder === "asc" ? "desc" : "asc",
    );
    setPriceChangeTickerSortOrder("asc"); // Reset ticker sort when value sort is toggled
  };

  return (
    <>
      <Navbar />

      <section className="trending-section">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <div className="header-section" style={{ flexDirection: 'column', gap: '20px' }}>
          <div className="header-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="header-left">
              <div className="live-market-badge">
                {/* <span className="dot"></span> LIVE MARKET */}
              </div>
              <h2 className="main-title">What's Trending</h2>
              <p className="sub-title">Real-time signals, market data, and curated intelligence</p>
            </div>
          </div>
        </div>

        {/* Sticky tabs */}
        <div className="trending-sticky-tabs">
          <div className="header-section" style={{ margin: 0, padding: 0 }}>
            <div className="info-group">
              <a
                href="#"
                className="premium-tour-btn"
                onClick={(e) => {
                  e.preventDefault();
                  handleInfoBox(highlightBoxEnum.PriceChange);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"></polygon>
                  <line x1="9" y1="3" x2="9" y2="18"></line>
                  <line x1="15" y1="6" x2="15" y2="21"></line>
                </svg>
                <span>What's this?</span>
              </a>
            </div>
          </div>
          <div className="right-controls" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="tabs-container">
            <button
              className={`tab-btn ${activeTab === 'Market Overview' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('Market Overview');
                scrollTabs('Market Overview');
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
              Market Overview
            </button>
            <button
              className={`tab-btn ${activeTab === 'Technical Signals' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('Technical Signals');
                scrollTabs('Technical Signals');
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
              Technical Signals
            </button>
            <button
              className={`tab-btn ${activeTab === 'News & Events' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('News & Events');
                scrollTabs('News & Events');
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"></path><path d="M18 14h-8"></path><path d="M15 18h-5"></path></svg>
              News & Events
            </button>
          </div>
          <span
            className="infoCircle"
            onClick={() => setShowDisclaimer(true)}
            style={{ cursor: "pointer", marginLeft: "10px" }}
          >
            <InfoCircleOutlined />
          </span>
        </div>
      </div>

        <div>
          {showDisclaimer && (
            <div
              className="modal-overlay trending-disclaimer-overlay"
              onClick={() => setShowDisclaimer(false)}
            >
              <div
                className="modal-box"
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
                ref={disclaimerModalRef}
                style={{ outline: "none" }}
              >
                <div className="modal-header-disclaimer">
                  <h3>Disclaimer & Information</h3>
                  <button
                    className="close-btn"
                    onClick={() => setShowDisclaimer(false)}
                    aria-label="Close modal"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <div className="footer-disclaimer">
                  <div className="disclaimer">
                    * Values are the highest/lowest values in the last 15 days.
                    <br />
                    In the <strong>New Value</strong> column, <span className="text-red">Red</span> color signifies the lowest value, whereas <span className="text-green">Green</span> stands for the highest value.
                  </div>

                  <div className="updated">Last Updated on: {data.date}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="trending-content">
          {showInfoBox && (
            <>
              <div className="modal-backdrop" onClick={() => setShowInfoBox(false)} />
              <div className="info-box-modal">
                <div className="info-box-header">
                  <div className="ib-title">PAGE TOUR</div>
                  <span className="close-x" onClick={() => setShowInfoBox(false)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </span>
                </div>
                <div className="info-box-content">
                  <p>{infoBoxText[highlightBox]}</p>
                </div>
                <div className="info-box-bottom">
                  <div className="pagination-container">
                    <span
                      className="nav-arrow"
                      onClick={() => sethighlightBox((prev) => {
                        const max = Object.keys(highlightBoxEnum).length - 1;
                        return prev === 0 ? max : prev - 1;
                      })}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                    </span>
                    <span className="page-info">
                      {highlightBox + 1} <span style={{ opacity: 0.5 }}>/</span>{" "}
                      {Object.keys(highlightBoxEnum).length}
                    </span>
                    <span
                      className="nav-arrow"
                      onClick={() => sethighlightBox((prev) => {
                        const max = Object.keys(highlightBoxEnum).length - 1;
                        return prev === max ? 0 : prev + 1;
                      })}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="grey-box">
            {/* Ticker filter Mobile view only */}
            <div className="only-mobile">
              <TickerFilter
                selectedTicker={tickerFilter ?? undefined}
                allTickers={tickerList}
                onChange={(value) => {
                  setTickerFilter(value);
                }}
              />
            </div>
            <div className={`main-trending-container ${activeTab === 'Technical Signals' ? 'technical-signals-layout' : ''}`} ref={splitContainerRef}>
              {/* First row: Stock vs Index + 1 cards */}
              <div id="leftSide-container">
                <div className="first-row-container" style={{ display: activeTab === 'News & Events' ? 'none' : '' }}>
                  {/* stock-index card */}
                  {activeTab === "Market Overview" && (
                    <StockVsIndexCard
                      data={data}
                      tickerFilter={tickerFilter}
                      isSearchMatch={isSearchMatch as any}
                      handleInfoBox={handleInfoBox}
                      showInfoBox={showInfoBox}
                      highlightBox={highlightBox}
                      highlightBoxEnum={highlightBoxEnum}
                      setInfoBoxRef={setInfoBoxRef}
                    />
                  )}
                </div>

                <div className="masonry-cards-container" style={{ display: activeTab === 'News & Events' ? 'none' : '' }}>
                  {/* Price Change card */}
                  {activeTab === "Market Overview" && (!tickerFilter || sortedPriceChangeValues.filter((key) => isSearchMatch(key)).length > 0) && (
                    <div style={{ gridColumn: 'span 1', width: '100%', height: '100%' }}>
                      <PriceCard
                        tooltipText={`Percent of change from previous day
                          Sorted by percent change`}
                        toggleInfoBox={() =>
                          handleInfoBox(highlightBoxEnum.PriceChange)
                        }
                        highlightBox={
                          showInfoBox &&
                          highlightBox === highlightBoxEnum.PriceChange
                        }
                        setInfoBoxRef={setInfoBoxRef(highlightBoxEnum.PriceChange)}
                        title={
                          <div className="svi-title-bar">
                            <div className="svi-title-left">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
                              </svg>
                              <span className="svi-title-text">Price Change </span>
                            </div>
                          </div>
                        }
                        headers={[
                          <div onClick={togglePriceChangeTickerSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }} key="header-1">
                            Stock Ticker {priceChangeTickerSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                          <div onClick={togglePriceChangeValueSort} style={{ cursor: "pointer", textAlign: "right", width: "100%", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }} key="header-2">
                            Value(%) {priceChangeValueSortOrder === "asc" ? "↑" : "↓"}
                          </div>
                        ]}
                        values={sortedPriceChangeValues
                          .filter((key) => isSearchMatch(key))
                          .map((key, i) => {
                            const value = data.price_change_alerts.top_n_price_change.stock_symbol[key];
                            const isRed = value < 0;
                            return [
                              <div
                                key={`${i}-ticker`}
                                className="ticker-name-trending"
                                onClick={() => {
                                  const ticker = key;
                                  if (ticker) {
                                    window.localStorage.setItem("selectedTab", "0");
                                    window.localStorage.setItem("tickerId", ticker);
                                    window.location.href = `/detail`;
                                  }
                                }}
                              >
                                {key}
                              </div>,
                              <div key={`${i}-value`} className="int-value" style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                <Chip
                                  size="small"
                                  label={`${isRed ? "" : "+"}${value.toFixed(2)}%`}
                                  sx={{
                                    borderRadius: "6px",
                                    color: isRed ? "#dc2626" : "#059669",
                                    backgroundColor: isRed ? "rgba(220,38,38,0.08)" : "rgba(5,150,105,0.08)",
                                    fontWeight: 700,
                                    fontSize: "11px",
                                    height: "26px",
                                    fontFamily: "'DM Sans', sans-serif",
                                  }}
                                />
                              </div>
                            ];
                          }).flat()}
                      />
                    </div>
                  )}
                  {/* Stock Volume card */}
                  {activeTab === "Market Overview" && (!tickerFilter || sortedVolumeValues.filter((key) => isSearchMatch(key.ticker_symbol)).length > 0) && (
                    <div style={{ gridColumn: 'span 1', width: '100%', height: '100%' }}>
                      <PriceCard
                        tooltipText="Stock volume alerts are based on minute-by-minute comparisons with the last 7 days. A high volume alert is triggered if the current volume exceeds the 7-day high for that exact minute, and vice versa for low volume."
                        toggleInfoBox={() =>
                          handleInfoBox(highlightBoxEnum.StockVolume)
                        }
                        highlightBox={
                          showInfoBox &&
                          highlightBox === highlightBoxEnum.StockVolume
                        }
                        setInfoBoxRef={setInfoBoxRef(highlightBoxEnum.StockVolume)}
                        title={
                          <div className="svi-title-bar">
                            <div className="svi-title-left">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="13" width="4" height="8" rx="1" /><rect x="10" y="9" width="4" height="12" rx="1" /><rect x="18" y="5" width="4" height="16" rx="1" />
                              </svg>
                              <span className="svi-title-text">Stock Volume</span>
                            </div>
                          </div>
                        }
                        headers={[
                          <div onClick={toggleVolumeSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            Stock Ticker {volumeSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                          <div onClick={toggleVolumeValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            Value (Lacs) {volumeValueSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                          <div onClick={toggleVolumeAlertSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            Alert {volumeAlertSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                        ]}
                        values={sortedVolumeValues
                          .filter(
                            (key) =>
                              !tickerFilter || key.ticker_symbol === tickerFilter,
                          )
                          .map((value, i) => [
                            <div
                              className="ticker-name-trending"
                              onClick={() => {
                                const ticker = value.ticker;
                                if (ticker) {
                                  window.localStorage.setItem("selectedTab", "0");
                                  window.localStorage.setItem("tickerId", ticker);
                                  window.location.href = `/detail`;
                                }
                              }}
                            >
                              <div key={`${i}-ticker`}>{value.ticker_symbol}</div>
                            </div>,
                            <div className="int-value" key={`${i}-value`}>
                              <Chip
                                size="small"
                                label={`${(value["Current Volume"] / 100000).toFixed(2)} L`}
                                sx={{
                                  borderRadius: "6px",
                                  color: "#0369a1",
                                  backgroundColor: "rgba(3,105,161,0.08)",
                                  fontWeight: 700,
                                  fontSize: "11px",
                                  height: "26px",
                                  fontFamily: "'DM Sans', sans-serif",
                                }}
                              />
                            </div>,
                            <div key={`${i}-minmax`}>
                              <Chip
                                size="small"
                                label={value.volume_type === "high_volume" ? "High" : "Low"}
                                sx={{
                                  borderRadius: "6px",
                                  color: value.volume_type === "high_volume" ? "#059669" : "#dc2626",
                                  backgroundColor: value.volume_type === "high_volume" ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)",
                                  fontWeight: 700,
                                  fontSize: "10px",
                                  height: "24px",
                                  fontFamily: "'DM Sans', sans-serif",
                                }}
                              />
                            </div>,
                          ])
                          .flat()}
                      />
                    </div>
                  )}
                  {/* Open Price Card */}
                  {activeTab === "Market Overview" && (!tickerFilter || sortedOpenPriceValues.filter((key) => isSearchMatch(key.ticker_symbol)).length > 0) && (
                    <div style={{ gridColumn: 'span 1', width: '100%', height: '100%' }}>
                      <PriceCard
                        tooltipText={`Identifies the highest and lowest open prices in the
                          last 15 days`}
                        toggleInfoBox={() =>
                          handleInfoBox(highlightBoxEnum.OpenPrices)
                        }
                        highlightBox={
                          showInfoBox &&
                          highlightBox === highlightBoxEnum.OpenPrices
                        }
                        setInfoBoxRef={setInfoBoxRef(highlightBoxEnum.OpenPrices)}
                        title={
                          <div className="svi-title-bar">
                            <div className="svi-title-left">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                              </svg>
                              <span className="svi-title-text">Open Prices</span>
                            </div>
                          </div>
                        }
                        headers={[
                          <div onClick={toggleOpenPriceSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            Stock Ticker {openPriceSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                          <div onClick={toggleOpenValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            Today's Price {openValueSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                          <div onClick={toggleOldValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            15-Day Limit {oldValueSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                        ]}
                        values={sortedOpenPriceValues
                          .filter(
                            (key) =>
                              !tickerFilter || key.ticker_symbol === tickerFilter,
                          )
                          .map((value, i) => {
                            const isRed = value.min_open !== undefined;

                            return [
                              <div
                                className="ticker-name-trending"
                                onClick={() => {
                                  const ticker = value.ticker_symbol;
                                  if (ticker) {
                                    window.localStorage.setItem("selectedTab", "0");
                                    window.localStorage.setItem("tickerId", ticker);
                                    window.location.href = `/detail`;
                                  }
                                }}
                              >
                                <div key={`${i}-ticker`}>{value.ticker_symbol}</div>
                              </div>,

                              <div className="int-value" key={`${i}-value`}>
                                <Chip
                                  size="small"
                                  label={value.live_day_open.toFixed(2)}
                                  sx={{
                                    borderRadius: "6px",
                                    color: isRed ? "#dc2626" : "#059669",
                                    backgroundColor: isRed ? "rgba(220,38,38,0.08)" : "rgba(5,150,105,0.08)",
                                    fontWeight: 700,
                                    fontSize: "11px",
                                    height: "26px",
                                    fontFamily: "'DM Sans', sans-serif",
                                  }}
                                />
                              </div>,
                              <div key={`${i}-minmax`}>
                                <Chip
                                  size="small"
                                  label={`${value.minmax.toFixed(2)} (${isRed ? "Low" : "High"})`}
                                  sx={{
                                    borderRadius: "6px",
                                    color: isRed ? "#dc2626" : "#059669",
                                    backgroundColor: isRed ? "rgba(220,38,38,0.08)" : "rgba(5,150,105,0.08)",
                                    fontWeight: 700,
                                    fontSize: "11px",
                                    height: "26px",
                                    fontFamily: "'DM Sans', sans-serif",
                                  }}
                                />
                              </div>,
                            ];
                          })
                          .flat()}
                      />
                    </div>
                  )}
                  {/* Close Prices Card */}
                  {activeTab === "Market Overview" && (!tickerFilter || sortedClosePriceValues.filter((key) => isSearchMatch(key.ticker_symbol)).length > 0) && (
                    <div style={{ gridColumn: 'span 1', width: '100%', height: '100%' }}>
                      <PriceCard
                        tooltipText={`Identifies the highest and lowest close prices in the
                          last 15 days`}
                        toggleInfoBox={() =>
                          handleInfoBox(highlightBoxEnum.LastTradedPrice)
                        }
                        highlightBox={
                          showInfoBox &&
                          highlightBox === highlightBoxEnum.LastTradedPrice
                        }
                        setInfoBoxRef={setInfoBoxRef(
                          highlightBoxEnum.LastTradedPrice,
                        )}
                        title={
                          <div className="svi-title-bar">
                            <div className="svi-title-left">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 20V10M12 20V4M6 20v-6" />
                              </svg>
                              <span className="svi-title-text">Close Prices</span>
                            </div>
                          </div>
                        }
                        headers={[
                          <div onClick={toggleClosePriceSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            Stock Ticker {closePriceSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                          <div onClick={toggleCloseValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            Today's Price {closeValueSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                          <div onClick={toggleOldCloseValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                            15-Day Limit {oldCloseValueSortOrder === "asc" ? "↑" : "↓"}
                          </div>,
                        ]}
                        values={sortedClosePriceValues
                          .filter(
                            (key) =>
                              !tickerFilter || key.ticker_symbol === tickerFilter,
                          )
                          .map((value, i) => {
                            const isRed = value.min_close !== undefined;

                            return [
                              <div
                                className="ticker-name-trending"
                                onClick={() => {
                                  const ticker = value.ticker_symbol;
                                  if (ticker) {
                                    window.localStorage.setItem("selectedTab", "0");
                                    window.localStorage.setItem("tickerId", ticker);
                                    window.location.href = `/detail`;
                                  }
                                }}
                              >
                                <div key={`${i}-ticker`}>{value.ticker_symbol}</div>
                              </div>,

                              <div className="int-value" key={`${i}-value`}>
                                <Chip
                                  size="small"
                                  label={value.live_day_close.toFixed(2)}
                                  sx={{
                                    borderRadius: "6px",
                                    color: isRed ? "#dc2626" : "#059669",
                                    backgroundColor: isRed ? "rgba(220,38,38,0.08)" : "rgba(5,150,105,0.08)",
                                    fontWeight: 700,
                                    fontSize: "11px",
                                    height: "26px",
                                    fontFamily: "'DM Sans', sans-serif",
                                  }}
                                />
                              </div>,
                              <div key={`${i}-minmax`}>
                                <Chip
                                  size="small"
                                  label={`${value.minmax.toFixed(2)} (${isRed ? "Low" : "High"})`}
                                  sx={{
                                    borderRadius: "6px",
                                    color: isRed ? "#dc2626" : "#059669",
                                    backgroundColor: isRed ? "rgba(220,38,38,0.08)" : "rgba(5,150,105,0.08)",
                                    fontWeight: 700,
                                    fontSize: "11px",
                                    height: "26px",
                                    fontFamily: "'DM Sans', sans-serif",
                                  }}
                                />
                              </div>,
                            ];
                          })
                          .flat()}
                      />
                    </div>
                  )}
                  {/* A/D Card */}
                  {activeTab === "Technical Signals" && (!tickerFilter || sortedAD.filter((value) => isSearchMatch(value.ticker_symbol)).length > 0) && (
                    <PriceCard
                      tooltipText={`The accumulation/distribution indicator (A/D) is a
                          cumulative indicator that uses volume and price to
                          assess whether a stock is being accumulated or
                          distributed. Triggers when current minute’s A/D value
                          exceeds or falls below the 7-day high or low A/D value
                          for that minute. Used to isolate short term cycles in
                          price movements.`}
                      toggleInfoBox={() =>
                        handleInfoBox(highlightBoxEnum.AccumulationDistribution)
                      }
                      highlightBox={
                        showInfoBox &&
                        highlightBox === highlightBoxEnum.AccumulationDistribution
                      }
                      setInfoBoxRef={setInfoBoxRef(
                        highlightBoxEnum.AccumulationDistribution,
                      )}
                      title={
                        <div className="svi-title-bar">
                          <div className="svi-title-left">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10M18 20V4M6 20v-6" /></svg>
                            <span className="svi-title-text">A/D</span>
                          </div>
                        </div>
                      }
                      headers={[
                        <div onClick={toggleAdTickerSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Stock Ticker {adSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleAdValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Value {adValueSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleAdAlertSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>Alerts {adAlertSortOrder === "asc" ? "↑" : "↓"}</div>,
                      ]}
                      values={sortedAD
                        .filter(
                          (value) => isSearchMatch(value.ticker_symbol),
                        )
                        .map((value, i) => [
                          <div
                            className="ticker-name-trending"
                            onClick={() => {
                              const ticker = value.ticker_symbol;
                              if (ticker) {
                                window.localStorage.setItem("selectedTab", "0");
                                window.localStorage.setItem("tickerId", ticker);
                                window.location.href = `/detail`;
                              }
                            }}
                            key={`${i}-ticker`}
                          >
                            <div>{value.ticker_symbol}</div>
                          </div>,
                          <div className="int-value" key={`${i}-value`}>
                            <Chip size="small" label={value["Current A/D"] ? value["Current A/D"].toFixed(2) : "N/A"} sx={{ borderRadius: "6px", color: value.AD_TYPE === "accumulation" ? "#059669" : "#dc2626", backgroundColor: value.AD_TYPE === "accumulation" ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)", fontWeight: 700, fontSize: "11px", height: "26px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                          <div key={`${i}-minmax`}>
                            <Chip size="small" label={value.AD_TYPE === "accumulation" ? "Accumulation" : "Distribution"} sx={{ borderRadius: "6px", color: value.AD_TYPE === "accumulation" ? "#059669" : "#dc2626", backgroundColor: value.AD_TYPE === "accumulation" ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)", fontWeight: 700, fontSize: "10px", height: "24px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                        ])
                        .flat()}
                    />
                  )}
                  {/* DPO Card */}
                  {activeTab === "Technical Signals" && (!tickerFilter || sortedDPO.filter((key) => isSearchMatch(key.ticker_symbol)).length > 0) && (
                    <PriceCard
                      tooltipText={` A detrended price oscillator, used in technical
                          analysis, strips out price trends in an effort to
                          estimate the length of price cycles from peak to peak
                          or trough to trough. Generates an alert if the current
                          DPO value exceeds or precedes the custom defined
                          threshold .`}
                      toggleInfoBox={() => handleInfoBox(highlightBoxEnum.DPO)}
                      highlightBox={
                        showInfoBox && highlightBox === highlightBoxEnum.DPO
                      }
                      setInfoBoxRef={setInfoBoxRef(highlightBoxEnum.DPO)}
                      title={
                        <div className="svi-title-bar">
                          <div className="svi-title-left">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h4l3-9 4 18 3-9h4" /></svg>
                            <span className="svi-title-text">DPO</span>
                          </div>
                        </div>
                      }
                      headers={[
                        <div onClick={toggleDpoTickerSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Stock Ticker {dpoSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleDpoValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Value {dpoValueSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleDpoAlertSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>Alerts {dpoAlertSortOrder === "asc" ? "↑" : "↓"}</div>,
                      ]}
                      values={sortedDPO
                        .filter(
                          (key) =>
                            !tickerFilter || key.ticker_symbol === tickerFilter,
                        )
                        .map((value, i) => [
                          <div
                            className="ticker-name-trending"
                            onClick={() => {
                              const ticker = value.ticker_symbol;
                              if (ticker) {
                                window.localStorage.setItem("selectedTab", "0");
                                window.localStorage.setItem("tickerId", ticker);
                                window.location.href = `/detail`;
                              }
                            }}
                          >
                            <div key={`${i}-ticker`}>{value.ticker_symbol}</div>
                          </div>,

                          <div className="int-value" key={`${i}-value`}>
                            <Chip size="small" label={value.Current_dpo?.toFixed(2)} sx={{ borderRadius: "6px", color: value.DPO_TYPE === "up" ? "#059669" : "#dc2626", backgroundColor: value.DPO_TYPE === "up" ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)", fontWeight: 700, fontSize: "11px", height: "26px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                          <div key={`${i}-minmax`}>
                            <Chip size="small" label={value.DPO_TYPE === "up" ? "Up" : "Down"} sx={{ borderRadius: "6px", color: value.DPO_TYPE === "up" ? "#059669" : "#dc2626", backgroundColor: value.DPO_TYPE === "up" ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)", fontWeight: 700, fontSize: "10px", height: "24px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                        ])
                        .flat()}
                    />
                  )}
                  {/* ATR Card */}
                  {activeTab === "Technical Signals" && (!tickerFilter || sortedAtrValues.filter((key) => isSearchMatch(key.ticker_symbol)).length > 0) && (
                    <PriceCard
                      tooltipText={`ATR (Average True Range) measures volatility. If the
                          current ATR exceeds the 14-day max, a high volatility
                          alert is triggered; if below the 14-day min, a low
                          volatility alert is generated.`}
                      toggleInfoBox={() =>
                        handleInfoBox(highlightBoxEnum.AverageTrueRange)
                      }
                      highlightBox={
                        showInfoBox &&
                        highlightBox === highlightBoxEnum.AverageTrueRange
                      }
                      setInfoBoxRef={setInfoBoxRef(
                        highlightBoxEnum.AverageTrueRange,
                      )}
                      title={
                        <div className="svi-title-bar">
                          <div className="svi-title-left">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
                            <span className="svi-title-text">ATR</span>
                          </div>
                        </div>
                      }
                      headers={[
                        <div onClick={toggleAtrSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Stock Ticker {atrSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleAtrValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Value {atrValueSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleAtrAlertSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>Volatility {atrAlertSortOrder === "asc" ? "↑" : "↓"}</div>,
                      ]}
                      values={sortedAtrValues
                        .filter(
                          (key) => isSearchMatch(key.ticker_symbol),
                        )
                        .map((value, i) => [
                          <div
                            className="ticker-name-trending"
                            onClick={() => {
                              const ticker = value.ticker_symbol;
                              if (ticker) {
                                window.localStorage.setItem("selectedTab", "0");
                                window.localStorage.setItem("tickerId", ticker);
                                window.location.href = "/detail";
                              }
                            }}
                          >
                            <div key={`${i}-ticker`}>{value.ticker_symbol}</div>
                          </div>,
                          <div className="int-value" key={`${i}-value`}>
                            <Chip size="small" label={value.Last_ATR.toFixed(2)} sx={{ borderRadius: "6px", color: value.volatilityType === "high_volatility" ? "#d97706" : "#64748b", backgroundColor: value.volatilityType === "high_volatility" ? "rgba(217,119,6,0.08)" : "rgba(100,116,139,0.08)", fontWeight: 700, fontSize: "11px", height: "26px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                          <div key={`${i}-minmax`}>
                            <Chip size="small" label={value.volatilityType === "high_volatility" ? "High" : "Low"} sx={{ borderRadius: "6px", color: value.volatilityType === "high_volatility" ? "#d97706" : "#64748b", backgroundColor: value.volatilityType === "high_volatility" ? "rgba(217,119,6,0.08)" : "rgba(100,116,139,0.08)", fontWeight: 700, fontSize: "10px", height: "24px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                        ])
                        .flat()}
                    />
                  )}
                  {/* ADX Card */}
                  {activeTab === "Technical Signals" && (!tickerFilter || sortedAdxValues.filter((key) => isSearchMatch(key.ticker_symbol)).length > 0) && (
                    <PriceCard
                      tooltipText={`ADX (Average Directional Index) measures trend
                          strength. If ADX exceeds 40, it indicates a strong
                          trend; below 15, it signals a weak trend, based on a
                          14-day comparison.`}
                      toggleInfoBox={() =>
                        handleInfoBox(highlightBoxEnum.AverageDirectionalIndex)
                      }
                      highlightBox={
                        showInfoBox &&
                        highlightBox === highlightBoxEnum.AverageDirectionalIndex
                      }
                      setInfoBoxRef={setInfoBoxRef(
                        highlightBoxEnum.AverageDirectionalIndex,
                      )}
                      title={
                        <div className="svi-title-bar">
                          <div className="svi-title-left">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /><path d="M17 7h4v4" /></svg>
                            <span className="svi-title-text">ADX</span>
                          </div>
                        </div>
                      }
                      headers={[
                        <div onClick={toggleAdxSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Stock Ticker {adxSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleAdxValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Value {adxValueSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleAdxAlertSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>Trend {adxAlertSortOrder === "asc" ? "↑" : "↓"}</div>,
                      ]}
                      values={sortedAdxValues
                        .filter(
                          (key) => isSearchMatch(key.ticker_symbol),
                        )
                        .map((value, i) => [
                          <div
                            className="ticker-name-trending"
                            onClick={() => {
                              const ticker = value.ticker_symbol;
                              if (ticker) {
                                window.localStorage.setItem("selectedTab", "0");
                                window.localStorage.setItem("tickerId", ticker);
                                window.location.href = `/detail`;
                              }
                            }}
                          >
                            <div key={`${i}-ticker`}>{value.ticker_symbol}</div>
                          </div>,
                          <div className="int-value" key={`${i}-value`}>
                            <Chip size="small" label={value.ADX.toFixed(2)} sx={{ borderRadius: "6px", color: value.trendType === "strong trend" ? "#059669" : "#64748b", backgroundColor: value.trendType === "strong trend" ? "rgba(5,150,105,0.08)" : "rgba(100,116,139,0.08)", fontWeight: 700, fontSize: "11px", height: "26px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                          <div key={`${i}-minmax`}>
                            <Chip size="small" label={value.trendType === "strong trend" ? "Strong" : "Weak"} sx={{ borderRadius: "6px", color: value.trendType === "strong trend" ? "#059669" : "#64748b", backgroundColor: value.trendType === "strong trend" ? "rgba(5,150,105,0.08)" : "rgba(100,116,139,0.08)", fontWeight: 700, fontSize: "10px", height: "24px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                        ])
                        .flat()}
                    />
                  )}
                  {/* VWAP Card */}
                  {activeTab === "Technical Signals" && (!tickerFilter || sortedVwapValues.filter((key) => isSearchMatch(key.ticker_symbol)).length > 0) && (
                    <PriceCard
                      tooltipText={`Signals an uptrend if the current VWAP surpasses the
                          14-day max, and a downtrend if below the 14-day min.`}
                      toggleInfoBox={() =>
                        handleInfoBox(highlightBoxEnum.VolumeWeightedAveragePrice)
                      }
                      highlightBox={
                        showInfoBox &&
                        highlightBox ===
                        highlightBoxEnum.VolumeWeightedAveragePrice
                      }
                      setInfoBoxRef={setInfoBoxRef(
                        highlightBoxEnum.VolumeWeightedAveragePrice,
                      )}
                      title={
                        <div className="svi-title-bar">
                          <div className="svi-title-left">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" /></svg>
                            <span className="svi-title-text">VWAP</span>
                          </div>
                        </div>
                      }
                      headers={[
                        <div onClick={toggleVwapSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Stock Ticker {vwapSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleVwapValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Value {vwapValueSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleVwapAlertSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>Trend {vwapAlertSortOrder === "asc" ? "↑" : "↓"}</div>,
                      ]}
                      values={sortedVwapValues
                        .filter(
                          (key) => isSearchMatch(key.ticker_symbol),
                        )
                        .map((value, i) => [
                          <div
                            className="ticker-name-trending"
                            onClick={() => {
                              const ticker = value.ticker_symbol;
                              if (ticker) {
                                window.localStorage.setItem("selectedTab", "0");
                                window.localStorage.setItem("tickerId", ticker);
                                window.location.href = `/detail`;
                              }
                            }}
                          >
                            <div key={`${i}-ticker`}>{value.ticker_symbol}</div>
                          </div>,
                          <div className="int-value" key={`${i}-value`}>
                            <Chip size="small" label={Number(value.current_vwap).toFixed(2)} sx={{ borderRadius: "6px", color: value.trend_type === "uptrend" ? "#059669" : "#dc2626", backgroundColor: value.trend_type === "uptrend" ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)", fontWeight: 700, fontSize: "11px", height: "26px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                          <div key={`${i}-minmax`}>
                            <Chip size="small" label={value.trend_type === "uptrend" ? "Up" : "Down"} sx={{ borderRadius: "6px", color: value.trend_type === "uptrend" ? "#059669" : "#dc2626", backgroundColor: value.trend_type === "uptrend" ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)", fontWeight: 700, fontSize: "10px", height: "24px", fontFamily: "'DM Sans', sans-serif" }} />
                          </div>,
                        ])
                        .flat()}
                    />
                  )}
                  {/* Momentum Change card */}
                  {activeTab === "Technical Signals" && (!tickerFilter || sortedMomentumValues.filter((key) => isSearchMatch(key.ticker_symbol)).length > 0) && (
                    <PriceCard
                      tooltipText={` Generated when RSI, MFI, and Williams %R
                          simultaneously signal oversold or overbought
                          conditions.`}
                      toggleInfoBox={() =>
                        handleInfoBox(highlightBoxEnum.MomentumChange)
                      }
                      highlightBox={
                        showInfoBox &&
                        highlightBox === highlightBoxEnum.MomentumChange
                      }
                      setInfoBoxRef={setInfoBoxRef(
                        highlightBoxEnum.MomentumChange,
                      )}
                      title={
                        <div className="svi-title-bar">
                          <div className="svi-title-left">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
                            <span className="svi-title-text">Momentum</span>
                          </div>
                        </div>
                      }
                      headers={[
                        <div onClick={toggleMomentumSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>
                          Stock Ticker {momentumSortOrder === "asc" ? "↑" : "↓"}
                        </div>,
                        <div onClick={toggleMomentumValueSort} style={{ cursor: "pointer", color: "#64748b", fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>Alerts {momentumValueSortOrder === "asc" ? "↑" : "↓"}</div>,
                      ]}
                      values={sortedMomentumValues
                        .filter(
                          (key) => isSearchMatch(key.ticker_symbol),
                        )
                        .map((value, i) => {
                          const isGreen = value.bought_type === "overbought";
                          return [
                            <div
                              className="ticker-name-trending"
                              onClick={() => {
                                const ticker = value.ticker_symbol;
                                if (ticker) {
                                  window.localStorage.setItem("selectedTab", "0");
                                  window.localStorage.setItem("tickerId", ticker);
                                  window.location.href = `/detail`;
                                }
                              }}
                            >
                              <div key={`${i}-ticker`}>{value.ticker_symbol}</div>
                            </div>,

                            <div key={`${i}-minmax`}>
                              <Chip size="small" label={value.Alert} sx={{ borderRadius: "6px", color: "#d97706", backgroundColor: "rgba(217,119,6,0.08)", fontWeight: 700, fontSize: "10px", height: "24px", fontFamily: "'DM Sans', sans-serif" }} />
                            </div>,
                          ];
                        })
                        .flat()}
                    />
                  )}
                </div>
                {/* Top Stories, News Count */}

                {/* Top Stories Card */}
                <div className="story-news-combo">
                  {activeTab === "News & Events" && (!tickerFilter || (data?.Top_stories_using_TSA_count && Object.keys(data.Top_stories_using_TSA_count).filter((key) => isSearchMatch(data.Top_stories_using_TSA_count[key]?.stock_symbol)).length > 0)) && (
                    <>
                      <div
                        className="top-stories-card"
                        style={{
                          position: "relative",
                          ...(showInfoBox &&
                            highlightBox === highlightBoxEnum.NewsStories
                            ? {
                              zIndex: 1000,
                              boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                            }
                            : {}),
                        }}
                        ref={(ref) =>
                        (infoBoxRefList.current[highlightBoxEnum.NewsStories] =
                          ref)
                        }
                      >
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0", padding: "16px", backgroundColor: "#f8fafc", borderTopLeftRadius: "0.5rem", borderTopRightRadius: "0.5rem", borderBottom: "1px solid lightgray" }}>
                            <span
                              className="infoCircleIcon"
                              data-tooltip-id="top-stories-tooltip"
                            >
                              <InfoCircleOutlined />
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>
                              <h3 className="heading" style={{ margin: 0, padding: 0, borderBottom: "none", fontSize: "1.3rem", fontWeight: "700", color: "#0f172a" }}>Top Stories</h3>
                            </div>
                            <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", letterSpacing: "0.05em", textTransform: "uppercase", marginRight: "28px" }}>
                              LATEST
                            </div>
                            <Tooltip
                              id="top-stories-tooltip"
                              place="top-start"
                              positionStrategy="fixed"
                              className="tooltip-section"
                            >
                              "Top Stories" features news with the corresponding
                              company mentioned over five times.
                            </Tooltip>
                          </div>
                        </>
                        <div className="stories-list">{renderTopStories()}</div>
                        <button
                          className="explore-more-button"
                          onClick={() => setOpenStoriesModal(true)}
                        >
                          Explore All
                        </button>
                      </div>
                      <Modal
                        open={openStoriesModal}
                        onClose={() => setOpenStoriesModal(false)}
                      >
                        <Box className="stories-modal">
                          <div className="sm-header">
                            <div className="sm-header-left">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>
                              <span className="sm-title">Top Stories</span>
                            </div>
                            <div className="sm-header-right">
                              <div className="sm-search-wrap">
                                <SearchIcon className="sm-search-icon" onClick={() => setShowSearch(!showSearch)} />
                                {showSearch && (
                                  <input
                                    type="text"
                                    className="sm-search-input"
                                    placeholder="Search stories..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    autoFocus
                                  />
                                )}
                              </div>
                              <div className="sm-filter-wrap">
                                <DateFilter onFilterChange={(filter) => setDateFilter(filter)} />
                              </div>
                              <button className="sm-close" onClick={() => setOpenStoriesModal(false)}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                              </button>
                            </div>
                          </div>

                          <div className="modal-stories-list">
                            {renderTopStories(undefined, true)}
                          </div>
                        </Box>
                      </Modal>
                    </>
                  )}

                  {/* News Count card starts*/}
                  {activeTab === "News & Events" && (!tickerFilter || (data?.news_count?.all_companies && Object.values(data.news_count.all_companies).filter((item) => isSearchMatch(item.stock_symbol)).length > 0)) && (
                    <div
                      className="news-count-card"
                      style={{
                        position: "relative",
                        paddingBottom: "15px",
                        backgroundColor: "#ffffff",

                        ...(showInfoBox &&
                          highlightBox === highlightBoxEnum.NewsCount
                          ? {
                            zIndex: 1000,
                            boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                          }
                          : {}),
                      }}
                      ref={(ref) =>
                      (infoBoxRefList.current[highlightBoxEnum.NewsCount] =
                        ref)
                      }
                    >
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0", padding: "16px", backgroundColor: "#f8fafc", borderTopLeftRadius: "0.5rem", borderTopRightRadius: "0.5rem", borderBottom: "1px solid lightgray" }}>
                          <span
                            className="infoCircleIcon"
                            data-tooltip-id="news-count-tooltip"
                          >
                            <InfoCircleOutlined />
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="20" x2="6" y2="14"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="18" y1="20" x2="18" y2="10"></line></svg>
                            <h3 className="heading" style={{ margin: 0, padding: 0, borderBottom: "none", fontSize: "1.3rem", fontWeight: "700", color: "#0f172a" }}>News Count</h3>
                          </div>
                          <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", letterSpacing: "0.05em", textTransform: "uppercase", marginRight: "28px" }}>
                            COVERAGE
                          </div>
                          <Tooltip
                            id="news-count-tooltip"
                            place="top-start"
                            positionStrategy="fixed"
                            className="tooltip-section"
                          >
                            Identifies the number of news articles published in
                            the last 24 hours and the weekly average.
                          </Tooltip>
                        </div>
                      </>

                      <div className="news-list">
                        <div className="news-table-wrapper">
                          <table className="news-table">
                            <thead>
                              <tr>
                                <th style={{ fontSize: "11px", fontWeight: "600", color: "#94a3b8", letterSpacing: "0.05em", padding: "12px", borderBottom: "1px solid #f1f5f9", textAlign: "center", borderLeft: "none", width: "40px" }}>
                                  S.NO
                                </th>
                                <th
                                  onClick={toggleStockNameSort}
                                  style={{ cursor: "pointer", fontSize: "11px", fontWeight: "600", color: "#94a3b8", letterSpacing: "0.05em", padding: "12px", borderBottom: "1px solid #f1f5f9", textAlign: "left", borderLeft: "none" }}
                                >
                                  STOCK{" "}
                                  {stockNameSortOrder &&
                                    (stockNameSortOrder === "asc" ? "↑" : "↓")}
                                </th>

                                <th
                                  onClick={toggleNewsSort}
                                  style={{ cursor: "pointer", fontSize: "11px", fontWeight: "600", color: "#94a3b8", letterSpacing: "0.05em", padding: "12px", borderBottom: "1px solid #f1f5f9", textAlign: "center", borderLeft: "none" }}
                                >
                                  NEWS{" "}
                                  {newsSortOrder &&
                                    (newsSortOrder === "asc" ? "↑" : "↓")}
                                </th>

                                <th
                                  onClick={toggleWeeklyAvgSort}
                                  style={{ cursor: "pointer", fontSize: "11px", fontWeight: "600", color: "#94a3b8", letterSpacing: "0.05em", padding: "12px", borderBottom: "1px solid #f1f5f9", textAlign: "right", borderLeft: "none" }}
                                >
                                  WEEKLY AVG{" "}
                                  {weeklyAvgSortOrder &&
                                    (weeklyAvgSortOrder === "asc" ? "↑" : "↓")}
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {data?.news_count?.all_companies ? (
                                (() => {
                                  const filteredNews = Object.values(
                                    data.news_count.all_companies,
                                  )
                                    .filter((item) => {
                                      if (!tickerFilter) return true;
                                      return item.stock_symbol === tickerFilter;
                                    })
                                    .sort((a, b) => {
                                      if (stockNameSortOrder) {
                                        return stockNameSortOrder === "asc"
                                          ? a.stock_symbol.localeCompare(
                                            b.stock_symbol,
                                          )
                                          : b.stock_symbol.localeCompare(
                                            a.stock_symbol,
                                          );
                                      }

                                      if (newsSortOrder) {
                                        return newsSortOrder === "asc"
                                          ? a.news_count - b.news_count
                                          : b.news_count - a.news_count;
                                      }

                                      if (weeklyAvgSortOrder) {
                                        return weeklyAvgSortOrder === "asc"
                                          ? a.avg_7days_news - b.avg_7days_news
                                          : b.avg_7days_news - a.avg_7days_news;
                                      }

                                      return 0;
                                    });

                                  return filteredNews.length === 0 ? (
                                    <tr>
                                      <td colSpan={4} className="no-data">
                                        NO DATA !
                                      </td>
                                    </tr>
                                  ) : (
                                    <>
                                      {filteredNews.map((item, i) => (
                                        <tr key={i} style={{ backgroundColor: "white" }}>
                                          <td style={{ padding: "16px 12px", borderBottom: "1px solid #f8fafc", borderLeft: "none", textAlign: "center", color: "#94a3b8", fontSize: "12px", fontWeight: "600" }}>
                                            {i + 1}
                                          </td>
                                          <td style={{ padding: "16px 12px", borderBottom: "1px solid #f8fafc", borderLeft: "none", textAlign: "left" }}>
                                            <Link
                                              style={{
                                                textDecoration: "none",
                                                color: "#0f172a",
                                                fontWeight: "700",
                                                fontSize: "13px"
                                              }}
                                              to={`/news/${item.stock_symbol}`}
                                            >
                                              {item.stock_symbol}
                                            </Link>
                                          </td>

                                          <td
                                            style={{
                                              padding: "16px 12px",
                                              borderBottom: "1px solid #f8fafc",
                                              borderLeft: "none",
                                              color: "#0f172a",
                                              fontWeight: "700",
                                              fontSize: "14px",
                                              textAlign: "center"
                                            }}
                                          >
                                            {item.news_count}
                                          </td>

                                          <td style={{ padding: "16px 12px", borderBottom: "1px solid #f8fafc", borderLeft: "none", textAlign: "right" }}>
                                            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px" }}>
                                              <span style={{ color: "#94a3b8", fontSize: "13px" }}>{Number(item.avg_7days_news).toFixed(2)}</span>
                                              <div style={{ width: "40px", height: "6px", backgroundColor: "#f1f5f9", borderRadius: "10px", overflow: "hidden" }}>
                                                <div style={{ width: `${Math.max(15, Math.min(100, (item.avg_7days_news / 30) * 100))}%`, height: "100%", backgroundColor: "#94a3b8", borderRadius: "10px" }} />
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </>
                                  );
                                })()
                              ) : (
                                <tr>
                                  <td colSpan={3} className="no-data">
                                    No data available
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* drag handle */}
              {activeTab !== 'Technical Signals' && (
                <div className="tp-drag-handle" onMouseDown={onPanelDragStart} title="Drag to resize" />
              )}

              {/* right side content */}
              <div id="rightSide-container" style={activeTab !== 'Technical Signals' ? { width: `${rightPanelWidth}%`, alignSelf: activeTab === 'News & Events' ? 'stretch' : undefined } : undefined}>
                {/* Ticker filter Desktop view - commented out to align Hot Events with left cards
                <div
                  className="only-desktop"
                  style={{
                    position: "relative",
                    ...(showInfoBox &&
                      highlightBox === highlightBoxEnum.FilterTicker
                      ? {
                        zIndex: 1000,
                        boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                      }
                      : {}),
                  }}
                  ref={(ref) =>
                  (infoBoxRefList.current[highlightBoxEnum.FilterTicker] =
                    ref)
                  }
                >
                  <TickerFilter
                    selectedTicker={tickerFilter ?? undefined}
                    allTickers={tickerList}
                    onChange={(value) => {
                      setTickerFilter(value);
                    }}
                  />
                </div> */}

                {/* Bottom section: Quotes on left, Hot Events/Top Stories/News Count on right */}
                <div className="bottom-section-container" style={{ height: activeTab === 'News & Events' ? '100%' : undefined }}>
                  {activeTab === "Market Overview" && (!tickerFilter || (data?.events_with_summary && Object.keys(data.events_with_summary).filter((key) => isSearchMatch(data.events_with_summary[key].stock_symbol)).length > 0)) && (
                    <div
                      className="events-pane container-events"
                      style={{
                        position: "relative",
                        ...(showInfoBox &&
                          highlightBox === highlightBoxEnum.HotEvents
                          ? {
                            zIndex: 1000,
                            boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                          }
                          : {}),
                      }}
                      ref={(ref) =>
                        (infoBoxRefList.current[highlightBoxEnum.HotEvents] = ref)
                      }
                    >
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "7px", padding: "0.7rem", borderBottom: "1px solid lightgray" }}>
                          <span
                            className="infoCircleIcon"
                            data-tooltip-id="hot-events-tooltip"
                          >
                            <InfoCircleOutlined />
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z" /></svg>
                            <h3 className="heading" style={{ margin: 0, padding: 0, borderBottom: "none", fontSize: "1.3rem", fontWeight: "700", color: "#0f172a" }}>Hot Events</h3>
                          </div>
                          <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", letterSpacing: "0.05em", textTransform: "uppercase", marginRight: "28px" }}>
                            BREAKING
                          </div>

                          <Tooltip
                            id="hot-events-tooltip"
                            place="top-start"
                            positionStrategy="fixed"
                            className="tooltip-section"
                          >
                            Identifies top news events and gathers related news
                            data to provide a summary for each company's most
                            important event.
                          </Tooltip>
                        </div>
                      </>
                      <div className="events-position">
                        <div className="events-flex">
                          {(() => {
                            const filteredEvents = Object.keys(
                              data.events_with_summary,
                            )
                              // Filter by ticker
                              .filter((key) => isSearchMatch(data.events_with_summary[key].stock_symbol));

                            return filteredEvents.map((key, i) => (
                              <TrendingEventCard
                                key={i}
                                title={`${key}`}
                                ticker={
                                  data.events_with_summary[key].stock_symbol
                                }
                                isHotEvent={true}
                                colorIndex={i}
                                events={[
                                  {
                                    title:
                                      data.events_with_summary[key].max_event,
                                    subtitle:
                                      data.events_with_summary[key].summary,
                                  },
                                ]}
                              />
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Quotes */}
                  {activeTab === "News & Events" && (!tickerFilter || quotes.filter((q) => q.quote !== "0" && q.context !== "0" && isSearchMatch(q.ticker_symbol, q.company_name, q.Person)).length > 0) && (
                    <div className="quotes-section" style={{ height: '100%' }}>
                      <div
                        className="quotes-container"
                        style={{
                          position: "relative",
                          ...(showInfoBox &&
                            highlightBox === highlightBoxEnum.Quotes
                            ? {
                              zIndex: 1000,
                              boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                            }
                            : {}),
                        }}
                        ref={(ref) =>
                          (infoBoxRefList.current[highlightBoxEnum.Quotes] = ref)
                        }
                      >
                        <div className="qc-header">
                          <div className="qc-header-left">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21z" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3z" /></svg>
                            <span className="qc-title">Quotes</span>
                          </div>
                          <span
                            className="qc-info-btn"
                            data-tooltip-id="quotes-tooltip"
                          >
                            <InfoCircleOutlined />
                          </span>
                          <Tooltip
                            id="quotes-tooltip"
                            place="top-start"
                            positionStrategy="fixed"
                            className="tooltip-section"
                          >
                            Identifies top Quotes and gathers related data to
                            provide a summary and context for each
                            company/person.
                          </Tooltip>
                        </div>
                        <div className="quotes-events-wrapper" style={{ position: 'relative', flex: 1, minHeight: 0 }}>
                          <div className="quotes-events-position" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto' }}>
                            <div className="quotes-events-flex">
                            {(() => {
                              const filteredQuotes = quotes.filter(
                                (quote) =>
                                  quote.quote !== "0" && quote.context !== "0" && isSearchMatch(quote.ticker_symbol, quote.company_name, quote.Person),
                              );

                              return filteredQuotes.length === 0 ? (
                                <div>
                                  <div className="no-data">No data available</div>
                                </div>
                              ) : (
                                filteredQuotes.map((quote, i) => (
                                  <a key={i} href={quote.link} target="_blank" rel="noopener noreferrer" className="qc-item">
                                    <div className="qc-item-top">
                                      <span className="qc-item-company">{quote.company_name}</span>
                                      <span className="qc-item-ticker">{quote.ticker_symbol}</span>
                                    </div>
                                    <blockquote className="qc-item-quote">"{quote.quote}"</blockquote>
                                    <p className="qc-item-context">{quote.context}</p>
                                    <div className="qc-item-person">- {quote.Person}</div>
                                  </a>
                                ))
                              );
                            })()}
                          </div>
                        </div>
                        </div>
                        <div className="explorMore-section">
                          <button
                            className="explore-more-button"
                            onClick={() => setOpenQuotesModal(true)}
                          >
                            Explore All
                          </button>
                        </div>
                        <Modal
                          open={openQuotesModal}
                          onClose={() => setOpenQuotesModal(false)}
                          aria-labelledby="quotes-modal-title"
                        >
                          <Box className="qm-modal">
                            <div className="sm-header">
                              <div className="sm-header-left">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21z" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3z" /></svg>
                                <span className="sm-title">All Quotes</span>
                              </div>
                              <div className="sm-header-right">
                                <div className="sm-search-wrap">
                                  <SearchIcon className="sm-search-icon" onClick={() => setShowSearch(!showSearch)} />
                                  {showSearch && (
                                    <input
                                      type="text"
                                      className="sm-search-input"
                                      placeholder="Search quotes..."
                                      value={modalSearchTicker}
                                      onChange={(e) => setModalSearchTicker(e.target.value)}
                                      autoFocus
                                    />
                                  )}
                                </div>
                                <button className="sm-close" onClick={() => setOpenQuotesModal(false)}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                </button>
                              </div>
                            </div>

                            <div className="qm-list">
                              {(() => {
                                const filteredQuotes = quotes
                                  .filter((quote) => quote.quote !== "0" && quote.context !== "0")
                                  .filter((quote) => {
                                    if (!modalSearchTicker) return true;
                                    const query = modalSearchTicker.toLowerCase();
                                    return Object.values(quote).some((val) =>
                                      String(val).toLowerCase().includes(query)
                                    );
                                  });

                                return filteredQuotes.length === 0 ? (
                                  <div className="no-data">No data available</div>
                                ) : (
                                  filteredQuotes.map((quote, i) => (
                                    <a key={i} href={quote.link} target="_blank" rel="noopener noreferrer" className="qm-card">
                                      <div className="qm-card-header">
                                        <span className="qm-company">{quote.company_name}</span>
                                        <span className="qm-ticker">{quote.ticker_symbol}</span>
                                      </div>
                                      <blockquote className="qm-quote">"{quote.quote}"</blockquote>
                                      <p className="qm-context">{quote.context}</p>
                                      <div className="qm-person">- {quote.Person}</div>
                                    </a>
                                  ))
                                );
                              })()}
                            </div>
                          </Box>
                        </Modal>
                      </div>
                    </div>
                  )}


                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <MiniFooter /> */}
    </>
  );
};

// const RedDownArrow = () => (
//   <svg
//     style={{
//       height: "1rem",
//       aspectRatio: "1/1",
//       marginLeft: "0.5rem",
//     }}
//     xmlns="http://www.w3.org/2000/svg"
//     viewBox="0 0 15 18"
//     fill="#ED0000"
//   >
//     <path
//       d="M7.57637 17.2019L0.583213 0.417997L14.5695 0.417996L7.57637 17.2019Z"
//       fill="#ED0000"
//     />
//   </svg>
// );

// const GreenUpArrow = () => (
//   <svg
//     style={{
//       height: "1rem",
//       aspectRatio: "1/1",
//       marginLeft: "0.5rem",
//     }}
//     xmlns="http://www.w3.org/2000/svg"
//     viewBox="0 0 15 18"
//     fill="#2C8745"
//   >
//     <path
//       d="M7.57598 0.908096L14.5691 17.692L0.582821 17.692L7.57598 0.908096Z"
//       fill="#2C8745"
//     />
//   </svg>
// );

export default Trending;
